// @ts-check
// File: app/src/utils/sessionWeather.js
//
// The weather where you trained, for sessions the Khronos Timer didn't measure
// (an outdoor ride, a phone-only session). Off unless switched on in Settings →
// Phone. The phone's location is rounded to about 1 km, sent only to
// Open-Meteo (free, no key, uses BOM data in Australia) and never saved: the
// session keeps just the temperature.

const OPEN_METEO_URL = "https://api.open-meteo.com/v1/forecast";
const TIMEOUT_MS = 8000;

/** Rounded to two decimals (about 1 km), so the lookup never pins a house. */
export const roundCoord = (/** @type {number} */ v) => Math.round(v * 100) / 100;

/**
 * @param {number} lat
 * @param {number} lon
 */
export const weatherUrl = (lat, lon) =>
  `${OPEN_METEO_URL}?latitude=${roundCoord(lat)}&longitude=${roundCoord(lon)}` +
  "&current=temperature_2m,relative_humidity_2m&timezone=auto";

/**
 * The saved weather block from an Open-Meteo response, or null when it has
 * no usable temperature.
 *
 * @param {any} body
 * @returns {{ temp_c: number, humidity_pct?: number, source: "open-meteo" } | null}
 */
export const parseWeather = (body) => {
  const t = body?.current?.temperature_2m;
  if (typeof t !== "number" || !Number.isFinite(t)) return null;
  const h = body?.current?.relative_humidity_2m;
  return {
    temp_c: Math.round(t * 10) / 10,
    ...(typeof h === "number" && Number.isFinite(h) ? { humidity_pct: Math.round(h) } : {}),
    source: "open-meteo",
  };
};

/**
 * The phone's position, coarse and possibly cached. Rejects when location is
 * unavailable or refused.
 *
 * @returns {Promise<{ lat: number, lon: number }>}
 */
export const coarsePosition = () =>
  new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("no geolocation"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      reject,
      { enableHighAccuracy: false, maximumAge: 30 * 60 * 1000, timeout: TIMEOUT_MS },
    );
  });

/**
 * Look up the temperature where the phone is now. Resolves null on any
 * failure: weather is a nice-to-have and must never get in a session's way.
 */
export const fetchSessionWeather = async (fetchImpl = (url, init) => window.fetch(url, init)) => {
  try {
    const { lat, lon } = await coarsePosition();
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
    try {
      const res = await fetchImpl(weatherUrl(lat, lon), { signal: ctrl.signal });
      if (!res.ok) return null;
      return parseWeather(await res.json());
    } finally {
      clearTimeout(timer);
    }
  } catch {
    return null;
  }
};

/**
 * How a saved session's temperature reads on its summary, or null when it
 * has none. The Timer's own reading comes first; the weather sits beside it.
 *
 * @param {any} summary a saved telemetry_summary
 * @returns {string | null}
 */
export const describeSessionTemperature = (summary) => {
  const env = summary?.env;
  const lo = env?.temp_min_c;
  const hi = env?.temp_max_c;
  const parts = [];
  if (typeof lo === "number" && typeof hi === "number") {
    const range = Math.round(lo) === Math.round(hi) ? `${Math.round(lo)}` : `${Math.round(lo)}–${Math.round(hi)}`;
    parts.push(`${range} °C on the Timer`);
  }
  const w = summary?.weather?.temp_c;
  if (typeof w === "number" && Number.isFinite(w)) parts.push(`${Math.round(w)} °C outside`);
  return parts.length ? parts.join(" · ") : null;
};
